import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, LifeBuoy, Coffee, Send, Copy, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SUPPORT_EMAIL = "Support@VoraStyle.com";

const SectionLabel = ({ children }) => (
  <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9CA47E] mb-3">{children}</div>
);

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("clipper");
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      toast.success("Email copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const send = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`[${topic}] ${name ? `from ${name}` : "VoraStyle help"}`);
    const body = encodeURIComponent(`Hi VoraStyle team,\n\n${message}\n\n— ${name || "(your name)"} (${email || "your@email.com"})`);
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="text-[#333333] min-h-screen">
      {/* Hero */}
      <section className="bg-white border-b border-[#EFEFEF]">
        <div className="max-w-[1100px] mx-auto px-6 py-16 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 md:col-span-7">
            <SectionLabel>◇ Contact</SectionLabel>
            <h1 className="font-heading font-medium text-5xl sm:text-6xl tracking-tight leading-[0.98]">
              We <span className="italic">read</span> every email.
            </h1>
            <p className="font-body text-[15px] sm:text-base text-[#595959] mt-5 max-w-xl leading-relaxed">
              Stuck on the Clipper? Found a bug? Want to feature your set on the homepage? Send us a note — a real human replies, usually within 24 hours.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5">
            <div className="border border-[#333333] bg-[#333333] text-white p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA47E]">◆ Email us</div>
              <div className="font-heading text-2xl mt-3 break-all" data-testid="contact-email">{SUPPORT_EMAIL}</div>
              <div className="flex gap-2 mt-5">
                <a
                  data-testid="contact-mailto"
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#9CA47E] hover:bg-[#7E876A] text-white font-mono text-[11px] uppercase tracking-[0.18em]"
                >
                  <Mail className="w-3.5 h-3.5" /> Open in mail
                </a>
                <button
                  data-testid="contact-copy"
                  onClick={copyEmail}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-transparent border border-white text-white hover:bg-white hover:text-[#333333] font-mono text-[11px] uppercase tracking-[0.18em] transition-colors"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three cards */}
      <section className="max-w-[1100px] mx-auto px-6 py-16">
        <SectionLabel>§ What's on your mind?</SectionLabel>
        <h2 className="font-heading text-4xl tracking-tight">Pick the right inbox.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {[
            {
              icon: <LifeBuoy className="w-5 h-5" />,
              title: "Clipper help",
              body: "Trouble downloading or installing the browser extension? Step-by-step support for any browser.",
              cta: "Email Support",
              subject: "[Clipper] Install help",
            },
            {
              icon: <MessageCircle className="w-5 h-5" />,
              title: "Bug or feedback",
              body: "Found a bug, broken image, or have a suggestion for the canvas editor? Tell us — we ship fast.",
              cta: "Email Support",
              subject: "[Bug/Feedback] ",
            },
            {
              icon: <Coffee className="w-5 h-5" />,
              title: "Partnerships",
              body: "Brand collabs, press, or wanting to feature your sets on the homepage? Drop us a line.",
              cta: "Email Support",
              subject: "[Partnership] ",
            },
          ].map((c) => (
            <a
              key={c.title}
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(c.subject)}`}
              className="group block bg-white border border-[#EFEFEF] hover:border-[#333333] transition-colors p-6"
            >
              <div className="w-10 h-10 bg-[#333333] text-white flex items-center justify-center group-hover:bg-[#9CA47E] transition-colors">
                {c.icon}
              </div>
              <div className="font-heading text-2xl mt-4">{c.title}</div>
              <p className="font-body text-sm text-[#595959] mt-2 leading-relaxed">{c.body}</p>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA47E] inline-flex items-center gap-1.5">
                {c.cta} <Send className="w-3 h-3" />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Compose form */}
      <section className="bg-white border-y border-[#EFEFEF]">
        <div className="max-w-[1100px] mx-auto px-6 py-16 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <SectionLabel>⌗ Or compose here</SectionLabel>
            <h2 className="font-heading text-4xl tracking-tight">Type once.<br />Send to <span className="italic">Support@VoraStyle.com</span>.</h2>
            <p className="font-body text-[#595959] mt-4 leading-relaxed">
              We'll open your default mail client pre-filled. Nothing is sent until you hit Send in your mail app — your draft never leaves your device.
            </p>
            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959] space-y-1">
              <div>· Reply within 24 hours</div>
              <div>· No bots, no tickets</div>
              <div>· Replies in your language</div>
            </div>
          </div>
          <form onSubmit={send} className="col-span-12 lg:col-span-7 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Your name</label>
                <Input data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-none border-[#E5E5E5] focus-visible:ring-0 focus-visible:border-[#333333] py-6 mt-2" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Reply email</label>
                <Input data-testid="contact-reply" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-[#E5E5E5] focus-visible:ring-0 focus-visible:border-[#333333] py-6 mt-2" />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Topic</label>
              <select
                data-testid="contact-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full mt-2 px-3 py-3.5 border border-[#E5E5E5] focus:outline-none focus:border-[#333333] font-body text-sm bg-white"
              >
                <option value="clipper">Clipper / extension help</option>
                <option value="bug">Bug or broken feature</option>
                <option value="feedback">Feature feedback / suggestion</option>
                <option value="partnership">Partnership / press</option>
                <option value="account">Account / billing</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Message</label>
              <textarea
                data-testid="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                placeholder="Tell us what's going on…"
                className="w-full mt-2 p-3 border border-[#E5E5E5] focus:outline-none focus:border-[#333333] font-body text-sm bg-white"
                required
              />
            </div>
            <Button
              data-testid="contact-send"
              type="submit"
              className="rounded-none w-full bg-[#9CA47E] hover:bg-[#7E876A] text-white py-6 font-mono text-[11px] uppercase tracking-[0.18em]"
            >
              <Send className="w-4 h-4 mr-2" /> Send to Support@VoraStyle.com
            </Button>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959] text-center">
              Opens your mail client · nothing is sent automatically
            </div>
          </form>
        </div>
      </section>

      {/* FAQ-ish footer band */}
      <section className="max-w-[1100px] mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="font-heading text-2xl">Already a member?</div>
            <p className="font-body text-sm text-[#595959] mt-2 leading-relaxed">
              Mention your username in the email and we'll find your account in seconds.
            </p>
            <Link to="/login" className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] underline underline-offset-4 text-[#9CA47E] inline-block">Sign in →</Link>
          </div>
          <div>
            <div className="font-heading text-2xl">Stuck on the Clipper?</div>
            <p className="font-body text-sm text-[#595959] mt-2 leading-relaxed">
              The 6-step install guide on the Clipper page covers 90% of issues.
            </p>
            <Link to="/clipper#install" className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] underline underline-offset-4 text-[#9CA47E] inline-block">Install guide →</Link>
          </div>
          <div>
            <div className="font-heading text-2xl">Want to support?</div>
            <p className="font-body text-sm text-[#595959] mt-2 leading-relaxed">
              VoraStyle stays open to everyone. The tip jar keeps the lights on.
            </p>
            <Link to="/support" className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] underline underline-offset-4 text-[#9CA47E] inline-block">Tip the collective →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
